/**
 * A photo of the meeting point.
 *
 * The last fifteen metres are a human problem, not a navigation one — the product already says
 * so, and hands over with "look around and wave". A picture of *what to look for* is the single
 * biggest improvement available to that moment: "the third lifeguard tower" is findable, and
 * "37.9838, 23.7275" is not.
 *
 * Photos are downscaled hard before they go anywhere. A phone camera produces four megabytes;
 * what this needs is something recognisable at arm's length in the sun, which is about forty
 * kilobytes. That matters twice over: a pin has to fit in localStorage, and a rally point has to
 * cross a festival network to seven other phones.
 *
 * Nothing here uploads anything. The caller decides where the string goes, and every current
 * caller keeps it either on the device or inside a session that expires.
 */

import { MAX_PHOTO_BUDGET_BYTES, MAX_PHOTO_CHARS } from '@beeline/shared';

/** Long edge, in pixels. Enough to recognise a gate, a tower or a tent. */
const MAX_EDGE_PX = 720;

/** JPEG rather than PNG: this is a photograph, and PNG would be ten times the size. */
const QUALITY = 0.72;

/**
 * A guard rail. Anything past this is not going to survive being put in localStorage.
 *
 * Taken from the protocol rather than restated, because the server rejects photos over the same
 * budget and a client limit even slightly above it would produce photos that capture cleanly,
 * upload cleanly, and quietly arrive at everybody else's phone without a picture.
 */
export const MAX_PHOTO_BYTES = MAX_PHOTO_BUDGET_BYTES;

export interface CapturedPhoto {
  /** A `data:image/jpeg;base64,...` string. Self-contained, so it needs no hosting anywhere. */
  dataUrl: string;
  bytes: number;
}

/**
 * Read a file the user picked, rotate-free and downscaled.
 *
 * `createImageBitmap` is used where it exists because it decodes off the main thread and honours
 * EXIF orientation, which is the difference between a photo and a photo lying on its side.
 */
export async function capturePhoto(file: File): Promise<CapturedPhoto | null> {
  if (!file.type.startsWith('image/')) return null;

  const bitmap = await decode(file);
  if (!bitmap) return null;

  try {
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);

    let quality = QUALITY;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);

    // A busy photograph can still come out too big at this size. Step the quality down rather
    // than refusing, because a slightly soft picture beats no picture.
    while (approximateBytes(dataUrl) > MAX_PHOTO_BYTES && quality > 0.35) {
      quality -= 0.12;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }

    const bytes = approximateBytes(dataUrl);
    return bytes > MAX_PHOTO_BYTES ? null : { dataUrl, bytes };
  } finally {
    if ('close' in bitmap) bitmap.close();
  }
}

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall through: older Safari rejects the options bag rather than ignoring it.
    }
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
}

/** Base64 carries four characters per three bytes; close enough to police a size limit with. */
export function approximateBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  return Math.floor(((dataUrl.length - comma - 1) * 3) / 4);
}

/**
 * Cheap sanity check on something read back out of storage or off the wire.
 *
 * The same ceiling the protocol enforces, so a photo that survives a round trip through a pin
 * cannot be one the relay would refuse.
 */
export const isPhotoDataUrl = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('data:image/') && value.length <= MAX_PHOTO_CHARS;

/**
 * An input that opens the camera on a phone and the file picker everywhere else.
 *
 * `capture` is a hint, not a demand: on a laptop it is ignored and the user gets to choose a
 * file, which is exactly right.
 */
export function createPhotoInput(onPhoto: (photo: CapturedPhoto | null) => void): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.capture = 'environment';
  input.hidden = true;

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    // Reset immediately, so picking the same file twice in a row still fires a change event.
    input.value = '';
    if (!file) return;

    void capturePhoto(file).then(onPhoto);
  });

  return input;
}
